import { Brain, Smartphone, Cloud, Code2, Palette, Server } from "lucide-react";
import { motion } from "framer-motion";

const services = [
  {
    id: "ai",
    title: "AI / ML Solutions",
    desc: "LLM agents, RAG pipelines, custom model fine-tuning, vision and NLP products that actually move your business metrics.",
    bullets: ["GPT-5 / Claude / Gemini integrations", "RAG & vector search", "Custom ML pipelines"],
    bg: "bg-[#FBD46D]",
    icon: Brain,
    span: "md:col-span-8",
    featured: true,
  },
  {
    id: "mobile",
    title: "Mobile App Development",
    desc: "Polished iOS & Android apps that users love. React Native & native builds.",
    bullets: ["React Native", "Swift / Kotlin", "App Store launches"],
    bg: "bg-[#4D96FF]",
    textLight: true,
    icon: Smartphone,
    span: "md:col-span-4",
  },
  {
    id: "saas",
    title: "Custom SaaS Development",
    desc: "Multi-tenant platforms with auth, billing, dashboards and everything you need to charge day one.",
    bg: "bg-[#FCE7F3]",
    icon: Cloud,
    span: "md:col-span-4",
    image: "https://static.prod-images.emergentagent.com/jobs/a8216672-e1e1-44b7-a0bd-e8478222755e/images/cef3007b851341f6af740871a9f2b9d698309894a6822bb782e1019e9e334d12.png",
  },
  {
    id: "web",
    title: "Web Development",
    desc: "Lightning-fast marketing sites, dashboards and internal tools.",
    bg: "bg-[#E0F2FE]",
    icon: Code2,
    span: "md:col-span-4",
  },
  {
    id: "uiux",
    title: "UI / UX Design",
    desc: "Distinctive product design that converts — not another template.",
    bg: "bg-[#6BCB77]",
    icon: Palette,
    span: "md:col-span-2",
  },
  {
    id: "devops",
    title: "Cloud & DevOps",
    desc: "AWS / GCP infra, CI/CD and observability baked in.",
    bg: "bg-white",
    icon: Server,
    span: "md:col-span-2",
  },
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 border-b-4 border-gray-900"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="inline-block bg-[#FF477E] text-white border-[3px] border-gray-900 rounded-full px-4 py-1 text-xs font-bold uppercase tracking-[0.2em] nb-shadow-sm">
              What we do
            </span>
            <h2 className="font-display mt-5 text-4xl sm:text-5xl md:text-6xl leading-tight">
              Six services.<br /> One obsession: delivering.
            </h2>
          </div>
          <p className="max-w-md text-lg font-medium text-gray-700">
            From a napkin sketch to a scaled product — we cover the full build,
            with AI and mobile as our sharpest blades.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {services.map((s, idx) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: idx * 0.06 }}
                data-testid={`service-card-${s.id}`}
                className={`${s.bg} ${s.span} border-[4px] border-gray-900 rounded-2xl nb-shadow nb-hover p-6 md:p-8 flex flex-col ${s.textLight ? "text-white" : "text-gray-900"}`}
              >
                <div className={`w-14 h-14 rounded-xl border-[3px] border-gray-900 flex items-center justify-center mb-5 ${s.textLight ? "bg-white text-gray-900" : "bg-white"}`}>
                  <Icon size={26} />
                </div>
                <h3 className="font-display text-2xl md:text-3xl mb-3 leading-tight">{s.title}</h3>
                <p className={`text-base md:text-lg font-medium ${s.textLight ? "text-white/90" : "text-gray-800"} leading-relaxed`}>
                  {s.desc}
                </p>
                {s.bullets && (
                  <ul className="mt-5 space-y-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-center gap-2 font-bold text-sm md:text-base">
                        <span className={`w-2.5 h-2.5 ${s.textLight ? "bg-white" : "bg-gray-900"} rounded-full`} />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
                {s.image && (
                  <img
                    src={s.image}
                    alt={s.title}
                    className="mt-6 w-full h-36 object-contain"
                  />
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
